export type ReportDialogKind =
  | 'incidents_rollup'
  | 'reliability_bundle'
  | 'proof_pack'
  | 'trust_pack'
  | 'cra_readiness'
  | 'site_readiness';

export type ReportDialogTask =
  | 'daily_ops_summary'
  | 'investigate_incident'
  | 'camera_acceptance'
  | 'site_acceptance'
  | 'customer_handover'
  | 'technical_reliability';

export type AcceptanceArtifactTask = Extract<
  ReportDialogTask,
  'camera_acceptance' | 'site_acceptance' | 'customer_handover' | 'investigate_incident'
>;

export const REPORT_TASK_TO_RECOMMENDED_KIND: Record<ReportDialogTask, ReportDialogKind> = {
  daily_ops_summary: 'incidents_rollup',
  investigate_incident: 'proof_pack',
  camera_acceptance: 'cra_readiness',
  site_acceptance: 'site_readiness',
  customer_handover: 'trust_pack',
  technical_reliability: 'reliability_bundle',
};

/** Tasks shown in task-first picker; CRA/SRA stay advanced-only per Epic 21. */
export const TASK_FIRST_ARTIFACT_TASKS: ReportDialogTask[] = (
  Object.keys(REPORT_TASK_TO_RECOMMENDED_KIND) as ReportDialogTask[]
).filter((task) => task !== 'camera_acceptance' && task !== 'site_acceptance');

export const isAdvancedOnlyArtifactTask = (task: ReportDialogTask): boolean =>
  task === 'camera_acceptance' || task === 'site_acceptance';

export const REPORT_TASK_LABELS: Record<ReportDialogTask, string> = {
  daily_ops_summary: 'Corrective action summary',
  investigate_incident: 'Incident evidence package',
  camera_acceptance: 'Camera readiness report',
  site_acceptance: 'Site readiness review',
  customer_handover: 'Platform trust pack',
  technical_reliability: 'Reliability review',
};

export const REPORT_TASK_HELP: Record<ReportDialogTask, string> = {
  daily_ops_summary:
    'Use the corrective action summary to review current queue signals, trends, and suppressions for follow-up planning.',
  investigate_incident: 'Use the Safety Report to declare one report scope and assemble the evidence, trust context, and supporting detail needed for a defensible conclusion.',
  camera_acceptance: 'Use the camera readiness report to validate observability and acceptance inputs for the selected task profile.',
  site_acceptance: 'Use the site readiness review to validate site-level readiness and installer acceptance inputs.',
  customer_handover: 'Use the platform trust pack to review generic platform controls, governance, and operational trust evidence.',
  technical_reliability: 'Use the reliability review for technical analysis of health, convergence, and reliability trends.',
};

export const REPORT_KIND_LABELS: Record<ReportDialogKind, string> = {
  incidents_rollup: 'Corrective action summary',
  reliability_bundle: 'Reliability review',
  proof_pack: 'Incident evidence package',
  trust_pack: 'Platform trust pack',
  cra_readiness: 'Camera readiness report',
  site_readiness: 'Site readiness review',
};

export const REPORT_KIND_TO_SUGGESTED_TASK: Record<ReportDialogKind, ReportDialogTask> = {
  incidents_rollup: 'daily_ops_summary',
  proof_pack: 'investigate_incident',
  cra_readiness: 'camera_acceptance',
  site_readiness: 'site_acceptance',
  trust_pack: 'customer_handover',
  reliability_bundle: 'technical_reliability',
};

export const getReportDialogFieldVisibility = (kind: ReportDialogKind) => ({
  usesTimeWindow:
    kind === 'incidents_rollup' ||
    kind === 'reliability_bundle' ||
    kind === 'proof_pack' ||
    kind === 'trust_pack',
  usesSiteField:
    kind === 'incidents_rollup' ||
    kind === 'reliability_bundle' ||
    kind === 'proof_pack' ||
    kind === 'trust_pack',
  showsIncidentFilters: kind === 'incidents_rollup',
  showsReliabilityFilters: kind === 'reliability_bundle',
  showsProofPackFilters: kind === 'proof_pack',
  showsCraSection: kind === 'cra_readiness',
  showsSraSection: kind === 'site_readiness',
  showsTrustPackSection: kind === 'trust_pack',
});

export const canRunArtifactAction = (
  kind: ReportDialogKind,
  canManageAcceptanceArtifacts: boolean
): boolean => kind === 'incidents_rollup' || canManageAcceptanceArtifacts;

export const inferReportTask = (kind: ReportDialogKind): ReportDialogTask => {
  return REPORT_KIND_TO_SUGGESTED_TASK[kind];
};

export const parseReportTaskParam = (raw: string | null | undefined): ReportDialogTask | null => {
  const v = String(raw || '')
    .trim()
    .toLowerCase();
  if (!v) return null;
  if (v === 'daily_ops_summary' || v === 'daily_ops' || v === 'daily' || v === 'ops' || v === 'live_ops_review')
    return 'daily_ops_summary';
  if (v === 'investigate_incident' || v === 'investigate' || v === 'incident' || v === 'incident_investigation')
    return 'investigate_incident';
  if (v === 'camera_acceptance' || v === 'camera' || v === 'cra' || v === 'readiness_validation')
    return 'camera_acceptance';
  if (v === 'site_acceptance' || v === 'site' || v === 'sra') return 'site_acceptance';
  if (v === 'customer_handover' || v === 'handover' || v === 'trust' || v === 'site_handover')
    return 'customer_handover';
  if (v === 'technical_reliability' || v === 'reliability' || v === 'bundle') return 'technical_reliability';
  return null;
};

export const parseReportKindParam = (raw: string | null | undefined): ReportDialogKind | null => {
  const v = String(raw || '')
    .trim()
    .toLowerCase();
  if (!v) return null;
  if (v === 'incidents_rollup' || v === 'rollup' || v === 'incidents') return 'incidents_rollup';
  if (v === 'reliability_bundle' || v === 'reliability' || v === 'bundle') return 'reliability_bundle';
  if (v === 'proof_pack' || v === 'proof' || v === 'proof-pack' || v === 'proofpack') return 'proof_pack';
  if (v === 'trust_pack' || v === 'trust' || v === 'trustpack') return 'trust_pack';
  if (v === 'cra_readiness' || v === 'cra' || v === 'camera_readiness' || v === 'readiness') return 'cra_readiness';
  if (v === 'site_readiness' || v === 'sra' || v === 'site') return 'site_readiness';
  return null;
};

/** Incident proof / artifact flows live on the Acceptance Proof workspace (not a modal). */
export const ACCEPTANCE_PROOF_ROUTE = '/acceptance/proof';
export const ACCEPTANCE_REPORT_ARTIFACT_ROUTE = '/acceptance/reports/artifacts';

export function buildSafetyReportArtifactPath(input: {
  reportId: string;
  orgId?: string | null;
  siteId?: string | null;
  workflowId?: string | null;
  deviceId?: string | null;
  zoneId?: string | null;
  cameraId?: string | null;
  reportFrom?: string | null;
  reportTo?: string | null;
  caseId?: string | null;
  incidentId?: string | null;
  policyType?: string | null;
  policyName?: string | null;
}): string {
  const reportId = String(input.reportId || '').trim();
  if (!reportId) return ACCEPTANCE_REPORT_ARTIFACT_ROUTE;
  const query = new URLSearchParams();
  if (input.orgId) query.set('org_id', String(input.orgId));
  if (input.siteId) query.set('site_id', String(input.siteId));
  if (input.workflowId) query.set('workflow_id', String(input.workflowId));
  if (input.deviceId) query.set('device_id', String(input.deviceId));
  if (input.zoneId) query.set('zone_id', String(input.zoneId));
  if (input.cameraId) query.set('camera_id', String(input.cameraId));
  if (input.reportFrom) query.set('report_from', String(input.reportFrom));
  if (input.reportTo) query.set('report_to', String(input.reportTo));
  if (input.caseId) query.set('case_id', String(input.caseId));
  if (input.incidentId) query.set('incident_id', String(input.incidentId));
  if (input.policyType) query.set('policy_type', String(input.policyType));
  if (input.policyName) query.set('policy_name', String(input.policyName));
  const encodedReportId = encodeURIComponent(reportId);
  const search = query.toString();
  return search
    ? `${ACCEPTANCE_REPORT_ARTIFACT_ROUTE}/${encodedReportId}?${search}`
    : `${ACCEPTANCE_REPORT_ARTIFACT_ROUTE}/${encodedReportId}`;
}

export function buildArtifactFlowPath(input: {
  kind: ReportDialogKind;
  orgId?: string | null;
  siteId?: string | null;
  task?: ReportDialogTask | null;
  deviceId?: string | null;
  zoneId?: string | null;
  cameraId?: string | null;
  reportFrom?: string | null;
  reportTo?: string | null;
  caseId?: string | null;
  incidentId?: string | null;
  workflowId?: string | null;
  safetyCaseId?: string | null;
  policyId?: string | null;
  policyType?: string | null;
  policyName?: string | null;
  proofScopeId?: string | null;
  includedIncidentCases?: string | number | null;
}): string {
  const query = new URLSearchParams();
  query.set('open_report', '1');
  query.set('report_kind', input.kind);
  if (input.task) query.set('report_task', input.task);
  if (input.orgId) query.set('org_id', String(input.orgId));
  if (input.siteId) query.set('site_id', String(input.siteId));
  if (input.deviceId) query.set('device_id', String(input.deviceId));
  if (input.zoneId) query.set('zone_id', String(input.zoneId));
  if (input.cameraId) query.set('camera_id', String(input.cameraId));
  if (input.reportFrom) query.set('report_from', String(input.reportFrom));
  if (input.reportTo) query.set('report_to', String(input.reportTo));
  if (input.caseId) query.set('case_id', String(input.caseId));
  if (input.incidentId) query.set('incident_id', String(input.incidentId));
  if (input.workflowId) query.set('workflow_id', String(input.workflowId));
  if (input.safetyCaseId) query.set('safety_case_id', String(input.safetyCaseId));
  if (input.policyId) query.set('policy_id', String(input.policyId));
  if (input.policyType) query.set('policy_type', String(input.policyType));
  if (input.policyName) query.set('policy_name', String(input.policyName));
  if (input.proofScopeId) query.set('proof_scope_id', String(input.proofScopeId));
  if (input.includedIncidentCases != null) query.set('included_incident_cases', String(input.includedIncidentCases));
  return `${ACCEPTANCE_PROOF_ROUTE}?${query.toString()}`;
}

export function resolveArtifactFlowPreset(
  params: URLSearchParams
): {
  kind: ReportDialogKind;
  task: ReportDialogTask;
  deviceId?: string;
  siteId?: string;
  zoneId?: string;
  cameraId?: string;
  reportFrom?: string;
  reportTo?: string;
  caseId?: string;
  incidentId?: string;
  policyId?: string;
  policyType?: string;
  policyName?: string;
  proofScopeId?: string;
  includedIncidentCases?: string;
  advancedArtifactPreset: boolean;
} | null {
  const raw = String(params.get('open_report') || '')
    .trim()
    .toLowerCase();
  if (!(raw === '1' || raw === 'true' || raw === 'yes')) return null;

  const kind = parseReportKindParam(params.get('report_kind')) || 'incidents_rollup';
  const explicitTask = parseReportTaskParam(params.get('report_task'));
  const task = explicitTask || inferReportTask(kind);
  const deviceId = String(params.get('device_id') || params.get('device') || '').trim() || undefined;
  const siteId = String(params.get('site_id') || '').trim() || undefined;
  const zoneId = String(params.get('zone_id') || '').trim() || undefined;
  const cameraId = String(params.get('camera_id') || '').trim() || undefined;
  const reportFrom = String(params.get('report_from') || '').trim() || undefined;
  const reportTo = String(params.get('report_to') || '').trim() || undefined;
  const caseId = String(params.get('case_id') || params.get('caseId') || '').trim() || undefined;
  const incidentId = String(params.get('incident_id') || '').trim() || undefined;
  const policyId = String(params.get('policy_id') || '').trim() || undefined;
  const policyType = String(params.get('policy_type') || params.get('incident_type') || '').trim() || undefined;
  const policyName = String(params.get('policy_name') || '').trim() || undefined;
  const proofScopeId = String(params.get('proof_scope_id') || params.get('proofScopeId') || '').trim() || undefined;
  const includedIncidentCases = String(params.get('included_incident_cases') || '').trim() || undefined;

  return {
    kind,
    task,
    deviceId,
    siteId,
    zoneId,
    cameraId,
    reportFrom,
    reportTo,
    caseId,
    incidentId,
    policyId,
    policyType,
    policyName,
    proofScopeId,
    includedIncidentCases,
    advancedArtifactPreset: Boolean(params.get('report_kind') && !explicitTask),
  };
}
